/**
 * Home Dashboard Types
 *
 * Two-sided home: view mode (widget strip + search results) and configure mode
 * (edit widgets + home search). See DOCS_V4/03-implementation/home-dashboard-two-sided-spec.md
 */

// ============================================================================
// Common widget IDs (canonical set from Doc 08 §4)
// ============================================================================

export const HOME_WIDGET_IDS = [
  'active_cycles',
  'active_tracks',
  'my_responsibilities',
  'items_requiring_attention',
  'quick_actions',
  'recent_publications',
  'recent_activity',
  // New widgets — off by default
  'intake_queue',
  'scoring_gaps',
  'readiness_blockers',
  'unresolved_dependencies',
  'upcoming_sessions',
  'cycle_readiness',
  'ball_position',
  'proposals_awaiting_review',
  'objectives_progress',
  'plans_of_record',
  'watchlist',
  'group_decisions',
  // Stage-aware progress widget — visible only when an active context exists
  'stage_progress',
  // Onboarding / getting-started action cards — on by default for all workspaces
  'getting_started',
  // Getting started step checklist (workspace setup progress)
  'getting_started_checklist',
  // How prioritization works explainer (3-step process)
  'how_it_works',
  // Prioritization toolkit (tools grid)
  'prioritization_toolkit',
  // App Layer launcher — the apps this user can open
  'apps',
  // Education widgets — off by default, users can add any or all
  'tips',             // short rotating feature tips
  'best_practices',   // workflow & process guidance
  'feature_spotlight',// one deeper feature highlighted at a time
  // Personal search shortcuts
  'saved_searches',   // links to saved search configurations
] as const;

export type HomeWidgetId = (typeof HOME_WIDGET_IDS)[number];

// ============================================================================
// Custom widget = search result widget (same data as main area, compact view)
// ============================================================================

/** How to render the widget's results in the strip */
export type CustomWidgetDisplayMode = 'list' | 'table';

/** Column placement: left (narrow strip), center, or right. In two-column layout, center+right render in the wider column. */
export type WidgetColumn = 'left' | 'center' | 'right';

/**
 * A custom widget is a configured search whose RESULTS are shown in the strip.
 * Same concept as the main display area: search config → results. The widget
 * is the "flipped" compact view (mini table, list, or later chart) of that search.
 */
export interface CustomWidgetDef {
  id: string;
  title: string;
  /** The search that drives this widget — same shape as main area search */
  search: HomeSearchConfig;
  /** How to show results in the widget strip */
  displayMode: CustomWidgetDisplayMode;
  /** Max rows in the widget (e.g. 5, 8, 10) */
  limit: number;
  /** Which column to show in; default center so custom widgets get more space */
  column?: WidgetColumn;
}

// ============================================================================
// Home search (drives the main display area)
// ============================================================================

export interface HomeSearchConfig {
  /** Reference to saved_views.id; when set, use that view's filters/sort/entityType */
  searchViewId?: string;
  /** Inline config when not using a saved view */
  entityType?: 'items' | 'cycles' | 'sessions' | 'focus_lists';
  filters?: Array<{ facetKey: string; operator: string; value: unknown; label?: string }>;
  sort?: { field: string; direction: 'asc' | 'desc' };
  viewMode?: 'list' | 'card' | 'table';
}

// ============================================================================
// Full home dashboard config (persisted per workspace or user)
// ============================================================================

export interface HomeDashboardConfig {
  /** Ordered list of common widget ids to show in the strip */
  widgetIds: HomeWidgetId[];
  /** Which column each common widget is in (default left) */
  widgetColumns?: Partial<Record<HomeWidgetId, WidgetColumn>>;
  /** When true for a widget id, hide the widget entirely when it has no data */
  widgetHideIfEmpty?: Partial<Record<HomeWidgetId, boolean>>;
  /** User-defined widgets */
  customWidgets: CustomWidgetDef[];
  /** Search that drives the main results area */
  search: HomeSearchConfig;
  /** Layout: two-column (left strip + wider main) or three-column (left, center, right equal) */
  layout?: 'two-column' | 'three-column';
  /**
   * Schema version for incremental widget migrations. When a new product release adds
   * default-on widgets, we bump WIDGET_SCHEMA_VERSION and the store adds missing widgets
   * to existing user configs exactly once. Absence (undefined) means "pre-versioning" and
   * triggers the v0→v1 migration that adds the Getting Started widget suite.
   */
  widgetSchemaVersion?: number;
  /** Last updated (for cache invalidation) */
  updatedAt?: string;
}

export const DEFAULT_HOME_SEARCH: HomeSearchConfig = {
  entityType: 'items',
  filters: [],
  sort: { field: 'updatedAt', direction: 'desc' },
  viewMode: 'list',
};

/**
 * Default layout: three equal columns, widgets grouped by theme.
 *
 * Left  — "Your Work"       : personal, immediately actionable items
 * Center — "Active Cycles"  : what's running, in motion, or coming up
 * Right  — "Pipeline Health": quality signals, intake, and activity
 *
 * Within each column, the most important widgets are listed first so they
 * render at the top. The overall widgetIds order is: left → center → right.
 */
export const DEFAULT_HOME_DASHBOARD_CONFIG: HomeDashboardConfig = {
  layout: 'three-column',
  widgetIds: [
    // ── Left: Your Work ─────────────────────────────────────────────────────
    'getting_started',          // quick actions widget (renamed from "Getting Started")
    'my_responsibilities',      // My Tasks (open assignments)
    'proposals_awaiting_review',// decisions waiting on you
    'ball_position',            // catchball — who has the ball?
    'watchlist',                // items you're tracking
    'saved_searches',           // personal saved search shortcuts

    // ── Center: Active Cycles ────────────────────────────────────────────────
    'getting_started_checklist',// workspace setup progress checklist
    'how_it_works',             // 3-step "How Prioritization Works" explainer
    'prioritization_toolkit',   // tools grid (stack ranking, scoring, matrices, etc.)
    'apps',                     // App Layer launcher — jobs, not tools
    'active_cycles',            // what's running right now
    'active_tracks',            // tracks in motion (lanes / comparability)
    'upcoming_sessions',        // what's coming up next
    'stage_progress',           // where are we in the cycle?
    'cycle_readiness',          // are we ready to run?
    'objectives_progress',      // OKR / objectives status
    'plans_of_record',          // strategic plans

    // ── Right: Pipeline Health ───────────────────────────────────────────────
    'items_requiring_attention',// top-priority signal: things needing action
    'intake_queue',             // incoming ideas and requests
    'scoring_gaps',             // items missing scores
    'readiness_blockers',       // items blocked from progressing
    'unresolved_dependencies',  // dependency tangles
    'recent_activity',          // what changed recently
    'recent_publications',      // recently published lists

    // ── Education (off by default — users add as desired) ────────────────────
    'tips',
    'best_practices',
    'feature_spotlight',
  ],
  widgetColumns: {
    // Left: Your Work
    getting_started: 'left',
    my_responsibilities: 'left',
    proposals_awaiting_review: 'left',
    ball_position: 'left',
    watchlist: 'left',
    saved_searches: 'left',
    // Center: Active Cycles
    getting_started_checklist: 'center',
    how_it_works: 'center',
    prioritization_toolkit: 'center',
    apps: 'center',
    active_cycles: 'center',
    active_tracks: 'center',
    upcoming_sessions: 'center',
    stage_progress: 'center',
    cycle_readiness: 'center',
    objectives_progress: 'center',
    plans_of_record: 'center',
    // Right: Pipeline Health
    items_requiring_attention: 'right',
    intake_queue: 'right',
    scoring_gaps: 'right',
    readiness_blockers: 'right',
    unresolved_dependencies: 'right',
    recent_activity: 'right',
    recent_publications: 'right',
    // Education — default to right column
    tips: 'right',
    best_practices: 'right',
    feature_spotlight: 'right',
  },
  widgetHideIfEmpty: {
    // Hide noisier health widgets when they have nothing to report
    scoring_gaps: true,
    readiness_blockers: true,
    unresolved_dependencies: true,
    proposals_awaiting_review: true,
    ball_position: true,
    // Hide saved searches when none exist yet
    saved_searches: true,
  },
  customWidgets: [],
  search: { ...DEFAULT_HOME_SEARCH },
  updatedAt: new Date().toISOString(),
};
